<template>
  <v-container fluid class="pa-4">
    <!-- Header Section -->
    <v-row class="mb-6">
      <v-col cols="12">
        <div class="d-flex align-center mb-2">
          <v-avatar size="56" class="mr-4 primary">
            <v-icon size="32" color="white">mdi-pill</v-icon>
          </v-avatar>
          <div>
            <h1 class="display-1 mb-1 font-weight-bold">Easy Drug Card</h1>
            <p class="subtitle-1 grey--text text--darken-1 mb-0">
              View and download Easy Drug cards for you and your dependents
            </p>
          </div>
        </div>
      </v-col>
    </v-row>
    
    <!-- Loading State -->
    <v-card v-if="loading" class="mb-4" elevation="2">
      <v-card-text class="pa-6">
        <div class="text-center">
          <v-progress-circular
            indeterminate
            color="primary"
            size="64"
            width="6"
            class="mb-4"
          ></v-progress-circular>
          <div class="title">Loading cards...</div>
          <div class="caption grey--text mt-2">Please wait while we fetch your card information</div>
        </div>
      </v-card-text>
    </v-card>
    
    <!-- Error State -->
    <v-card v-else-if="error" class="mb-4" elevation="2" outlined>
      <v-alert type="error" prominent border="left" colored-border>
        <v-alert-title class="headline">Error</v-alert-title>
        <div class="mt-2">{{ error }}</div>
      </v-alert>
    </v-card>
    
    <!-- Empty State -->
    <v-card v-else-if="members.length === 0" class="mb-4" elevation="2" outlined>
      <v-card-text class="pa-6 text-center">
        <v-icon size="80" color="grey lighten-1" class="mb-4">mdi-card-off</v-icon>
        <h2 class="headline mb-2">No Cards Found</h2>
        <p class="body-1 grey--text">
          No active Easy Drug cards found for your account.
        </p>
      </v-card-text>
    </v-card>
    
    <!-- Cards List -->
    <div v-else>
      <v-row>
        <v-col 
          v-for="member in allMembers" 
          :key="member.MEMBER_ID"
          cols="12" 
          md="6" 
          lg="4"
        >
          <v-card class="fill-height d-flex flex-column" elevation="3">
            <v-card-title class="primary white--text py-3">
              <v-icon left color="white">
                {{ member.RELATIONSHIP === 'Self' ? 'mdi-account-star' : 'mdi-account' }}
              </v-icon>
              {{ member.FIRST_NAME }} {{ member.LAST_NAME }}
              <v-spacer></v-spacer>
              <v-chip x-small color="white" text-color="primary">
                {{ member.RELATIONSHIP === 'Self' ? 'Subscriber' : 'Dependent' }}
              </v-chip>
            </v-card-title>
            
            <v-card-text class="flex-grow-1 pa-4">
              <!-- Card Preview -->
              <div :id="`card-${member.MEMBER_ID}`" class="insurance-card-preview mb-4">
                <div class="card-header">
                  <div class="card-logo">
                    <!-- Placeholder for Logo -->
                    <v-icon size="32" color="primary">mdi-pill</v-icon>
                    <span class="font-weight-bold ml-2 primary--text">FamilyHealthChoice</span>
                  </div>
                  <div class="card-title">Easy Drug Card</div>
                </div>
                
                <v-divider class="my-2"></v-divider>
                
                <div class="card-body">
                  <v-row dense>
                    <v-col cols="12">
                      <div class="caption grey--text">Member Name</div>
                      <div class="font-weight-bold text-uppercase">{{ member.FIRST_NAME }} {{ member.LAST_NAME }}</div>
                    </v-col>
                    
                    <v-col cols="6">
                      <div class="caption grey--text">Member ID</div>
                      <div class="font-weight-bold">{{ member.MEMBER_ID }}</div>
                    </v-col>
                    
                    <v-col cols="6">
                      <div class="caption grey--text">Group Number</div>
                      <div class="font-weight-bold">{{ formatPlanName(member.details && member.details.enrollment && member.details.enrollment.planName) || 'N/A' }}</div>
                    </v-col>
                    
                    <v-col cols="12" v-if="member.details && member.details.enrollment && member.details.enrollment.pcp">
                      <div class="caption grey--text">PCP Name</div>
                      <div class="font-weight-bold">{{ member.details.enrollment.pcp.FIRST_NAME }} {{ member.details.enrollment.pcp.LAST_NAME }}</div>
                    </v-col>
                    
                    <v-col cols="12" v-if="member.details && member.details.enrollment && member.details.enrollment.pcp && member.details.enrollment.pcp.PHONE">
                      <div class="caption grey--text">PCP Phone</div>
                      <div class="font-weight-bold">{{ member.details.enrollment.pcp.PHONE }}</div>
                    </v-col>
                    
                    <v-col cols="6">
                      <div class="caption grey--text">Effective Date</div>
                      <div class="font-weight-bold">
                        {{ getEffectiveDate(member) }}
                      </div>
                    </v-col>
                    
                    <v-col cols="6">
                      <div class="caption grey--text">RxBin</div>
                      <div class="font-weight-bold">004336</div>
                    </v-col>
                  </v-row>
                </div>
                
                <div class="card-footer mt-4">
                  <div class="caption text-center grey--text">
                    This card is for identification purposes only.
                  </div>
                </div>
              </div>
            </v-card-text>
            
            <v-divider></v-divider>
            
            <v-card-actions class="pa-4">
              <v-btn 
                block 
                color="primary" 
                @click="downloadCard(member)"
                :loading="downloading === member.MEMBER_ID"
              >
                <v-icon left>mdi-download</v-icon>
                Download Card
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
    </div>
    
    <!-- Snackbar -->
    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="snackbar.timeout"
      top
    >
      {{ snackbar.message }}
      <template v-slot:action="{ attrs }">
        <v-btn
          color="white"
          text
          v-bind="attrs"
          @click="snackbar.show = false"
        >
          Close
        </v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
import axios from 'axios';
import moment from 'moment';
import { formatPlanNameForDisplay } from '../utils/planNameDisplay';

export default {
  name: 'EasyDrugCard',
  data() {
    return {
      members: [], // Grouped by family
      allMembers: [], // Flat list for display
      loading: true,
      error: null,
      insurance: '',
      downloading: null,
      snackbar: {
        show: false,
        message: '',
        color: 'success',
        timeout: 3000
      }
    };
  },
  async mounted() {
    await this.getUser();
    if (this.insurance) {
      await this.loadMembers();
    } else {
      this.loading = false;
      this.error = 'No insurance information found. Please contact support.';
    }
  },
  methods: {
    formatPlanName(val) {
      return formatPlanNameForDisplay(val);
    },
    async getUser() {
      try {
        const response = await axios.get('/api/user');
        if (response.data && response.data.length > 0) {
          const user = response.data[0];
          if (user.insurance && user.insurance.length > 0) {
            this.insurance = user.insurance[0];
          }
        }
      } catch (error) {
        this.error = 'Error loading user data. Please try again.';
        this.loading = false;
      }
    },
    async loadMembers() {
      this.loading = true;
      this.error = null;
      try {
        const timestamp = Date.now();
        const url = `/api/enrollment/members/${this.insurance}?t=${timestamp}`;
        const response = await axios.get(url);
        
        this.members = response.data || [];
        
        // Flatten the list and fetch details for each
        const flatList = [];
        for (const family of this.members) {
          if (family.subscriber) {
            flatList.push({ ...family.subscriber, RELATIONSHIP: 'Self' });
          }
          if (family.dependents && family.dependents.length > 0) {
            family.dependents.forEach(dep => {
              flatList.push({ ...dep, RELATIONSHIP: dep.RELATIONSHIP || 'Dependent' });
            });
          }
        }
        
        // Fetch details for each member in parallel
        const detailPromises = flatList.map(async (member) => {
          try {
            const detailRes = await axios.get(`/api/enrollment/member/${this.insurance}/${member.MEMBER_ID}`);
            return { ...member, details: detailRes.data };
          } catch (e) {
            console.error(`Error fetching details for ${member.MEMBER_ID}`, e);
            return { ...member, details: {} };
          }
        });
        
        this.allMembers = await Promise.all(detailPromises);
        
      } catch (error) {
        this.error = 'Error loading members. Please try again.';
        console.error(error);
      } finally {
        this.loading = false;
      }
    },
    formatDate(date) {
      if (!date) return '';
      return moment(date).format('MM/DD/YYYY');
    },
    getEffectiveDate(member) {
      // Effective date is the enrollment approval date, not PCP date
      // Try multiple paths to find the effective date
      const date1 = member.details && member.details.member && member.details.member.EFFECTIVE_DATE;
      const date2 = member.details && member.details.member && member.details.member.PCP_EFFECTIVE_DATE; // Fallback
      const date3 = member.EFFECTIVE_DATE || member.PCP_EFFECTIVE_DATE; // Fallback
      
      const effectiveDate = date1 || date2 || date3;
      const formatted = this.formatDate(effectiveDate);
      return formatted || 'N/A';
    },
    async downloadCard(member) {
      this.downloading = member.MEMBER_ID;
      
      try {
        // PDF gerado no portal (Mongo + pdfService); não depende do SonderDashboard
        const url = `/api/enrollment/members/${this.insurance}/easy-drug-card/${member.MEMBER_ID}`;
        
        const response = await axios.get(url, { responseType: 'blob' });
        
        // Create blob link to download
        const fileURL = window.URL.createObjectURL(new Blob([response.data]));
        const fileLink = document.createElement('a');
        
        fileLink.href = fileURL;
        fileLink.setAttribute('download', `EasyDrugCard_${member.FIRST_NAME}_${member.LAST_NAME}.pdf`);
        document.body.appendChild(fileLink);
        
        fileLink.click();
        
        // Cleanup
        document.body.removeChild(fileLink);
        window.URL.revokeObjectURL(fileURL);
        
        this.snackbar.message = 'Easy Drug Card downloaded successfully';
        this.snackbar.color = 'success';
        this.snackbar.show = true;
      } catch (e) {
        console.error('Error downloading PDF', e);
        this.snackbar.message = 'Error downloading Easy Drug Card PDF';
        this.snackbar.color = 'error';
        this.snackbar.show = true;
      } finally {
        this.downloading = null;
      }
    }
  }
};
</script>

<style scoped>
.insurance-card-preview {
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 16px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.05);
  background-image: linear-gradient(135deg, #ffffff 0%, #f5f7fa 100%);
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.card-logo {
  display: flex;
  align-items: center;
}

.card-title {
  font-size: 0.8rem;
  color: #757575;
  text-transform: uppercase;
  letter-spacing: 1px;
}

.card-body {
  margin-top: 12px;
}
</style>
